import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Chip,
  LinearProgress,
  Link,
  Typography,
} from "@mui/material";
import { GridColDef, GridRenderCellParams, DataGrid } from "@mui/x-data-grid";
import { useClassroom } from "../../../context/ClassroomContext";
import {
  getClassroom,
  getStudentProgress,
} from "../../../services/classroomService";
import { useHandleError } from "../../../hooks/useHandleError";

interface StudentProgressRow {
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  overallProgress: number;
  currentWeek: number;
  lastActivity: string | null;
}

const getStatusInfo = (
  lastActivity: string | null,
  overallProgress: number
): { label: string; color: "success" | "warning" | "error" | "default" } => {
  if (!lastActivity) return { label: "No Activity", color: "default" };
  const daysSince = Math.floor(
    (Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24)
  );
  if (daysSince > 7) return { label: `Idle ${daysSince}d`, color: "warning" };
  if (overallProgress < 40) return { label: "Behind", color: "error" };
  return { label: "On Track", color: "success" };
};

const getProgressColor = (value: number): "success" | "warning" | "error" => {
  if (value >= 70) return "success";
  if (value >= 40) return "warning";
  return "error";
};

const StudentProgress: React.FC = () => {
  const { classroomId } = useParams<{ classroomId: string }>();
  const navigate = useNavigate();
  const handleError = useHandleError();
  const { classroom, setClassroom } = useClassroom();

  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState<StudentProgressRow[]>([]);

  useEffect(() => {
    if (!classroomId) return;
    if (!classroom || classroom._id !== classroomId) {
      getClassroom(classroomId)
        .then((res) => res.data && setClassroom(res.data))
        .catch(handleError);
    }
  }, [classroomId]);

  useEffect(() => {
    if (!classroomId) return;
    setLoading(true);
    getStudentProgress(classroomId)
      .then((res) => setStudents(res.data || []))
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [classroomId]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Student",
      width: 200,
      valueGetter: (params) =>
        `${params.row.firstName} ${params.row.lastName}`,
    },
    { field: "email", headerName: "Email", width: 230 },
    {
      field: "overallProgress",
      headerName: "Progress",
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number;
        return (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={value}
              color={getProgressColor(value)}
              sx={{ flex: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" sx={{ minWidth: 35 }}>
              {Math.round(value)}%
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "currentWeek",
      headerName: "Week",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "lastActivity",
      headerName: "Last Active",
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return "Never";
        return new Date(params.value).toLocaleDateString("en-AU");
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const { label, color } = getStatusInfo(
          params.row.lastActivity,
          params.row.overallProgress
        );
        return (
          <Chip
            label={label}
            color={color}
            size="small"
            sx={{ fontSize: ".7rem" }}
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: "pointer" }}
          onClick={() => navigate("/teacher/classrooms")}
        >
          Classrooms
        </Link>
        <Typography color="text.primary">
          {classroom?.classroomName || "..."} — Student Progress
        </Typography>
      </Breadcrumbs>

      <Typography variant="h5" gutterBottom>
        Student Progress
      </Typography>

      <DataGrid
        rows={students}
        columns={columns}
        getRowId={(row) => row.studentId}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        onRowClick={(params) =>
          navigate(
            `/teacher/classrooms/${classroomId}/students/${params.row.studentId}`
          )
        }
        sx={{ cursor: "pointer", "& .MuiDataGrid-row:hover": { backgroundColor: "action.hover" } }}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[10, 25, 50]}
      />
    </Box>
  );
};

export default StudentProgress;
